import React, { useCallback } from "react";
import {
  Box,
  Typography,
  Grid2,
  Collapse,
  useTheme,
  Button,
  useMediaQuery,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { colorSystem } from "../../theme";
import WorkoutHistorySet from "../client-exercise-history/workout-history/WorkoutHistorySet";
import { ExerciseTypeExtra } from "../../constants";
import WorkoutCardBase from "./WorkoutCardBase";
import { List } from "lucide-react";
//import { TitleWithSuperset } from "./TitleWithSuperset";
import dayjs from "dayjs";
import { DATE_FORMAT_DDMMYYYY } from "../../utils/date";
import {
  CLIENT_EXERCISE_HISTORY_ROUTE,
  COACH_CLIENT_EXERCISE_HISTORY_ROUTE,
  COACH_PROGRAMS_ROUTE,
} from "../../routes/routes";
import { useNavigate } from "react-router-dom";
import { useUserIsClient } from "../../hooks/useCurrentUser";
import {
  UserExerciseDto,
  ExerciseAssetDto,
} from "@growth-machine-llc/stridist-api-client";
import { ArrowForward } from "@mui/icons-material";
import { defaultSectionTitle } from "../workout/utils";

const isSuperSet = (currentExercise, nextExercise) => {
  if (!nextExercise) return false;
  return (
    currentExercise?.superset &&
    nextExercise?.superset &&
    currentExercise.superset === nextExercise.superset
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  scrollContainer: {
    maxHeight: "70vh",
    overflowY: "auto",
    paddingRight: theme.spacing(1),
    marginRight: theme.spacing(-1),
    scrollbarWidth: "thin",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: colorSystem.gray4,
      borderRadius: 3,
    },
  },
  container: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  exerciseTitle: {
    fontSize: 16,
    lineHeight: "24px",
    fontWeight: "bold",
    paddingLeft: theme.spacing(2),
    margin: theme.spacing(0),
  },
  divider: {
    width: "100%",
    height: 1,
    backgroundColor: colorSystem.gray5,
    marginTop: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: 16,
    lineHeight: "24px",
    fontWeight: "bold",
    padding: theme.spacing(1.5, 2),
    backgroundColor: colorSystem.blue5,
    borderRadius: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1.5),
  },
  noResult: {
    fontSize: 16,
    lineHeight: "20px",
    fontWeight: "bold",
    paddingLeft: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  exerciseContainer: {
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  workoutSummaryBtn: {
    fontSize: "0.75rem",
    padding: `${theme.spacing(0.25)} ${theme.spacing(1)}`,
    color: theme.palette.primary.main,
  },
  exerciseHistoryBtn: {
    fontSize: "0.75rem",
    color: theme.palette.primary.main,
    minHeight: 0,
    lineHeight: 1,
    padding: 0,
  },
}));
const workoutHistorySetStyles = makeStyles((theme) => ({
  body: {
    display: "flex",
    maxHeight: 42,
    gap: 20,
    alignItems: "center",
    borderRadius: 4,
    padding: theme.spacing(0.5, 0),
    paddingLeft: theme.spacing(2),
    marginBottom: theme.spacing(0),
  },
  unitText: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  smallCircle: {
    width: 28,
    height: 28,
    fontSize: 12,
  },
}));

type ClientWorkoutsChooseProps = {
  onClose: () => void;
  username?: string;
  selectWorkout: {
    title: string;
    date: string;
    completedAt?: string;
    session: number;
    min: number;
    value: any[];
    mindValue: string;
    bodyValue: string;
    feel: string;
    time: number;
    programSlug: string;
    componentSlug: string;
  };
};
type ExerciseState = {
  selectedExercise: UserExerciseDto;
};

const ClientWorkoutsChoose = ({
  onClose,
  username,
  selectWorkout,
}: ClientWorkoutsChooseProps) => {
  const s = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const isClient = useUserIsClient();
  const sWorkoutSet = workoutHistorySetStyles();
  const [open, setOpen] = React.useState(false);
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  // set a slight delay to trigger the collapse animation
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setOpen(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const historyPath = isClient
    ? CLIENT_EXERCISE_HISTORY_ROUTE
    : COACH_CLIENT_EXERCISE_HISTORY_ROUTE.replace(":username", username);

  const createUserExerciseDto = useCallback((item: any) => {
    const asset = new ExerciseAssetDto({
      id: item.exercise.assetId,
      name: item.exercise.title,
    });

    return new UserExerciseDto({
      id: 0,
      title: item.exercise.title,
      exercise: asset,
    });
  }, []);

  const handleExerciseSelect = useCallback(
    (exercise: any) => {
      navigate(historyPath, {
        state: {
          //set the data not from the asset, but from the exercise result content
          selectedExercise: createUserExerciseDto(exercise),
        } as ExerciseState,
      });
    },
    [historyPath, navigate, createUserExerciseDto],
  );

  const handleOpenWorkoutSummary = useCallback(() => {
    if (!selectWorkout?.componentSlug) {
      return;
    }
    const date = dayjs(selectWorkout.date).format("YYYY-MM-DD");
    const programSlug = selectWorkout.programSlug ?? "default-program";
    const componentSlug = selectWorkout.componentSlug;

    let fullPath: string;

    if (!isClient) {
      const baseRoute = COACH_PROGRAMS_ROUTE;

      fullPath = `${baseRoute}/summary/${programSlug}/${date}/${componentSlug}/${username}`;
    } else {
      fullPath = `/programs/summary/${programSlug}/${date}/${componentSlug}`;
    }

    navigate(fullPath);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [selectWorkout, isClient, username, navigate]);

  return (
    <Box className={s.root}>
      <>
        <WorkoutCardBase
          title={selectWorkout.title}
          subtitle={`Recorded ${dayjs(selectWorkout.completedAt).format(DATE_FORMAT_DDMMYYYY)}`}
          footerStats={{
            mind: selectWorkout.mindValue,
            body: selectWorkout.bodyValue,
            feel: selectWorkout.feel,
            time: selectWorkout.min,
          }}
          onClick={onClose}
          onClose={onClose}
          type="close"
          footerActions={
            !isXs && (
              <Button
                size="small"
                endIcon={<ArrowForward width={16} height={16} />}
                onClick={handleOpenWorkoutSummary}
                className={s.workoutSummaryBtn}
              >
                See more
              </Button>
            )
          }
        />
        {Array.isArray(selectWorkout.value) &&
        selectWorkout.value.length > 0 ? (
          <Collapse in={open} timeout={400} unmountOnExit>
            <Box className={s.scrollContainer}>
              <Grid2 container spacing={2} className={s.container}>
                {selectWorkout.value.map((item, index) => {
                  const shouldShowSectionTitle =
                    item.title?.trim() || selectWorkout.value.length > 1;
                  return (
                    <Grid2 key={index} size={{ xs: 12 }}>
                      {shouldShowSectionTitle && (
                        <Typography
                          className={s.sectionTitle}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <List size={24} color={colorSystem.gray} />
                          {item.title?.trim() || defaultSectionTitle}
                        </Typography>
                      )}

                      {Array.isArray(item.result) &&
                        item.result.map((exercise: any, exIndex: number) => (
                          <Box key={exIndex} sx={{ mb: 1 }}>
                            <Box className={s.exerciseContainer}>
                              <Typography className={s.exerciseTitle}>
                                {exercise.title}
                              </Typography>
                              <Button
                                size="small"
                                variant="text"
                                endIcon={<ArrowForward />}
                                className={s.exerciseHistoryBtn}
                                onClick={() =>
                                  handleExerciseSelect(
                                    new UserExerciseDto({
                                      id: 0,
                                      title: exercise.title,
                                      exercise: exercise,
                                    }),
                                  )
                                }
                              >
                                View history
                              </Button>
                            </Box>

                            {Array.isArray(exercise.result) &&
                              exercise.result.map(
                                (set: any, setIndex: number) => (
                                  <WorkoutHistorySet
                                    key={setIndex}
                                    setNumber={setIndex}
                                    set={set}
                                    typeExtraMeasurement={
                                      exercise?.typeExtraMeasurement ||
                                      ExerciseTypeExtra.NONE
                                    }
                                    exerciseTypeSet={exercise.typeSet}
                                    units={exercise.units}
                                    classes={{
                                      root: sWorkoutSet.body,
                                      unitText: sWorkoutSet.unitText,
                                    }}
                                    circleClassName={sWorkoutSet.smallCircle}
                                  />
                                ),
                              )}
                          </Box>
                        ))}
                    </Grid2>
                  );
                })}
              </Grid2>
            </Box>
          </Collapse>
        ) : (
          <Typography className={s.noResult}>No results</Typography>
        )}
      </>
    </Box>
  );
};
export default React.memo(ClientWorkoutsChoose);
